import { useState } from "react";
import { ChevronDown, Check, Star } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../client/components/ui/popover";
import { cn } from "../client/utils";
import useTranslation from "../client/hooks/useTranslation";

export interface AccountFilterValue {
  type: "all" | "all-inactive" | "none" | "account" | "group";
  id?: string;
}

interface AccountFilterOption {
  id: string;
  name: string;
  effectivePropFirm?: string | null;
  isActive?: boolean;
}

interface GroupFilterOption {
  id: string;
  name: string;
  accounts: AccountFilterOption[];
}

interface AccountGroupFilterProps {
  accounts: AccountFilterOption[];
  groups: GroupFilterOption[];
  value: AccountFilterValue;
  onChange: (value: AccountFilterValue) => void;
  className?: string;
  pinnedValue?: AccountFilterValue | null;
  onPinValue?: (value: AccountFilterValue) => void;
}

function encode(value: AccountFilterValue): string {
  if (value.type === "all") return "__all__";
  if (value.type === "all-inactive") return "__all_inactive__";
  if (value.type === "none") return "__none__";
  if (value.type === "group") return `group:${value.id}`;
  return `account:${value.id}`;
}

function isEqual(a: AccountFilterValue, b: AccountFilterValue): boolean {
  return a.type === b.type && a.id === b.id;
}

function triggerLabel(value: AccountFilterValue, accounts: AccountFilterOption[], groups: GroupFilterOption[], t: any): string {
  if (value.type === "all") return t.tradingAccounts.filterAllAccounts;
  if (value.type === "all-inactive") return t.tradingAccounts.filterAllAccountsWithInactive;
  if (value.type === "none") return t.tradingAccounts.filterNoAccount;
  if (value.type === "group") {
    const g = groups.find((g) => g.id === value.id);
    return g ? `📁 ${g.name}` : t.tradingAccounts.filterAllAccounts;
  }
  const a = accounts.find((a) => a.id === value.id);
  return a ? `${a.isActive === false ? "⬜" : "🟢"} ${a.name}` : t.tradingAccounts.filterAllAccounts;
}

interface RowProps {
  label: string;
  isSelected: boolean;
  isPinned: boolean;
  onSelect: () => void;
  onPin?: () => void;
  indent?: number;
  muted?: boolean;
}

function FilterRow({ label, isSelected, isPinned, onSelect, onPin, indent = 0, muted = false }: RowProps) {
  return (
    <div
      className={cn(
        "group flex items-center gap-1 rounded-sm px-2 py-1.5 text-sm cursor-pointer select-none",
        "hover:bg-accent hover:text-accent-foreground",
        isSelected && "bg-accent/50",
        muted && "text-muted-foreground"
      )}
      style={{ paddingLeft: indent ? `${indent * 12 + 8}px` : undefined }}
      onClick={onSelect}
    >
      <Check className={cn("h-3.5 w-3.5 shrink-0 mr-1", isSelected ? "opacity-100" : "opacity-0")} />
      <span className="flex-1 truncate">{label}</span>
      {onPin && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPin(); }}
          className={cn(
            "flex-shrink-0 flex h-5 w-5 items-center justify-center rounded transition-colors",
            "opacity-0 group-hover:opacity-100",
            isPinned ? "opacity-100 text-yellow-400 hover:text-yellow-500" : "text-muted-foreground hover:text-yellow-400"
          )}
        >
          <Star className={cn("h-3 w-3", isPinned && "fill-yellow-400")} />
        </button>
      )}
    </div>
  );
}

export function AccountGroupFilter({
  accounts,
  groups,
  value,
  onChange,
  className,
  pinnedValue,
  onPinValue,
}: AccountGroupFilterProps) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);

  const groupAccountIds = new Set(groups.flatMap((g) => g.accounts.map((a) => a.id)));
  const ungroupedAccounts = accounts.filter((a) => !groupAccountIds.has(a.id));

  const select = (v: AccountFilterValue) => { onChange(v); setOpen(false); };
  const pin = onPinValue ? (v: AccountFilterValue) => onPinValue(v) : undefined;

  const label = triggerLabel(value, accounts, groups, t);

  const isActive = value.type !== "all";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center justify-between gap-2 rounded-md border px-3 h-9 font-medium text-sm transition-colors",
            isActive
              ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
              : "bg-background text-foreground border-border hover:bg-accent hover:text-accent-foreground",
            className ?? "w-[220px]"
          )}
        >
          <span className="truncate">{label}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] max-w-[calc(100vw-2rem)] p-1 max-h-[min(560px,70vh)] overflow-y-auto" align="start">
        <FilterRow
          label={t.tradingAccounts.filterAllAccounts}
          isSelected={value.type === "all"}
          isPinned={!!pinnedValue && isEqual(pinnedValue, { type: "all" })}
          onSelect={() => select({ type: "all" })}
          onPin={pin ? () => pin({ type: "all" }) : undefined}
        />
        <FilterRow
          label={t.tradingAccounts.filterAllAccountsWithInactive}
          isSelected={value.type === "all-inactive"}
          isPinned={!!pinnedValue && isEqual(pinnedValue, { type: "all-inactive" })}
          onSelect={() => select({ type: "all-inactive" })}
          onPin={pin ? () => pin({ type: "all-inactive" }) : undefined}
          muted
        />
        <FilterRow
          label={t.tradingAccounts.filterNoAccount}
          isSelected={value.type === "none"}
          isPinned={!!pinnedValue && isEqual(pinnedValue, { type: "none" })}
          onSelect={() => select({ type: "none" })}
          onPin={pin ? () => pin({ type: "none" }) : undefined}
          muted
        />

        {groups.map((group) => (
          <div key={group.id}>
            <div className="px-2 pt-2 pb-0.5 text-xs font-medium text-muted-foreground">📁 {group.name}</div>
            <FilterRow
              label={`${t.tradingAccounts.filterByGroup}: ${group.name}`}
              isSelected={value.type === "group" && value.id === group.id}
              isPinned={!!pinnedValue && isEqual(pinnedValue, { type: "group", id: group.id })}
              onSelect={() => select({ type: "group", id: group.id })}
              onPin={pin ? () => pin({ type: "group", id: group.id }) : undefined}
              indent={1}
              muted
            />
            {group.accounts.map((account) => (
              <FilterRow
                key={account.id}
                label={`${account.isActive === false ? "⬜" : "🟢"} ${account.name}`}
                isSelected={value.type === "account" && value.id === account.id}
                isPinned={!!pinnedValue && isEqual(pinnedValue, { type: "account", id: account.id })}
                onSelect={() => select({ type: "account", id: account.id })}
                onPin={pin ? () => pin({ type: "account", id: account.id }) : undefined}
                indent={2}
              />
            ))}
          </div>
        ))}

        {ungroupedAccounts.length > 0 && (
          <div>
            <div className="px-2 pt-2 pb-0.5 text-xs font-medium text-muted-foreground">{t.tradingAccounts.ungrouped}</div>
            {ungroupedAccounts.map((account) => (
              <FilterRow
                key={account.id}
                label={`${account.isActive === false ? "⬜" : "🟢"} ${account.name}`}
                isSelected={value.type === "account" && value.id === account.id}
                isPinned={!!pinnedValue && isEqual(pinnedValue, { type: "account", id: account.id })}
                onSelect={() => select({ type: "account", id: account.id })}
                onPin={pin ? () => pin({ type: "account", id: account.id }) : undefined}
                indent={1}
              />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
